import {useCallback} from 'react';

import {useDebouncedValue} from '../../util/useDebouncedValue';
import {makeContext} from '../MakeContext/makeContext';
import {PanelRunComparerProps} from './types';

export const MAX_SEARCH_STRING_HISTORY = 10;

const getNewSearchHistory = (
  newValue: string | undefined,
  prevSearchHistory: (string | undefined)[] | undefined
) => [
  newValue,
  ...(prevSearchHistory
    ?.filter(v => v !== newValue)
    ?.slice(0, MAX_SEARCH_STRING_HISTORY - 1) ?? []),
];

export const useRunComparer = (props: PanelRunComparerProps) => {
  const {config, updateConfig} = props;
  const {searchStringHistory: savedSearchStringHistory} = config;

  const handleSearchHistoryChange = useCallback(
    (newValue: (string | undefined)[] | undefined) => {
      updateConfig({
        searchStringHistory: newValue,
      });
    },
    [updateConfig]
  );

  // store the search history in local state so that we don't have to wait for the config to save in the backend
  const {
    value: localSearchStringHistory,
    debouncedSetValue,
    instantSetValue,
  } = useDebouncedValue<(string | undefined)[] | undefined>(
    savedSearchStringHistory,
    1000, // debounce to avoid re-constructing the tree on every keystroke
    handleSearchHistoryChange
  );
  const localSearchString = localSearchStringHistory?.[0];
  const debouncedAddValueToSearchStringHistory = useCallback(
    (newValue: string | undefined) =>
      debouncedSetValue(
        getNewSearchHistory(newValue, localSearchStringHistory)
      ),
    [debouncedSetValue, localSearchStringHistory]
  );
  const addValueToSearchStringHistory = useCallback(
    (newValue: string | undefined) =>
      instantSetValue(getNewSearchHistory(newValue, localSearchStringHistory)),
    [instantSetValue, localSearchStringHistory]
  );

  return {
    localSearchString,
    debouncedAddValueToSearchStringHistory,
    addValueToSearchStringHistory,
    localSearchStringHistory,
  };
};

export const {
  Provider: RunComparerContextProvider,
  hook: useRunComparerContext,
} = makeContext<ReturnType<typeof useRunComparer>>({
  contextType: 'RunComparer',
});
